import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, useLang, formatApiError, usePerm } from "../lib/providers";
import { PageHeader, Btn, EmptyState, TextInput } from "../components/primitives";
import { useConfirm } from "../components/ConfirmDialog";
import QuotesTabBar from "../components/QuotesTabBar";
import { Plus, PencilSimple, Trash, MagnifyingGlass, FilePdf, Ruler, FileText, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AufmasseListPage() {
  const { t, lang } = useLang();
  const confirm = useConfirm();
  const { can } = usePerm();
  const canCreate = can("aufmasse", "create");
  const canEdit = can("aufmasse", "edit");
  const canDelete = can("aufmasse", "delete");
  const canExport = can("aufmasse", "export");
  const canCreateInvoice = can("invoices", "create");
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/aufmasse").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (id, number) => {
    const ok = await confirm({
      title: lang === "de" ? `Aufmaß ${number} löschen?` : `Delete Aufmaß ${number}?`,
      description: lang === "de" ? "Diese Aktion kann nicht rückgängig gemacht werden." : "This cannot be undone.",
      confirmText: lang === "de" ? "Löschen" : "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/aufmasse/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success(lang === "de" ? "Gelöscht" : "Deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadPdf = async (id, number) => {
    try {
      const res = await api.get(`/aufmasse/${id}/pdf`, { responseType: "blob", params: { lang } });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${number}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const convertToInvoice = async (id, number) => {
    const ok = await confirm({
      title: lang === "de" ? `Rechnung aus Aufmaß ${number} erstellen?` : `Create invoice from Aufmaß ${number}?`,
      description: lang === "de"
        ? "Es wird eine neue Rechnung mit den Mengen aus diesem Aufmaß erzeugt. Preise werden – sofern verfügbar – aus dem zugehörigen Angebot übernommen."
        : "A new invoice will be created using the quantities from this Aufmaß. Prices are taken from the linked quote where available.",
      confirmText: lang === "de" ? "Rechnung erstellen" : "Create invoice",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/aufmasse/${id}/convert-to-invoice`);
      toast.success(lang === "de" ? `Rechnung ${data.number} erstellt` : `Invoice ${data.number} created`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const ql = q.trim().toLowerCase();
  const filtered = !ql ? items : items.filter((x) =>
    [x.number, x.title, x.notes, x.site_address, x.customer_snapshot?.name, x.customer_snapshot?.company]
      .some((v) => (v || "").toLowerCase().includes(ql))
  );

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow={lang === "de" ? "02 / Angebote · Aufmaße" : "02 / Quotes · Measurement"}
        title={lang === "de" ? "Aufmaße" : "Measurements"}
        description={lang === "de"
          ? "Erfasse zusätzliche Leistungen vor Ort: Freitext-Positionen ohne Preise, mit Unterschrift und PDF-Export."
          : "Record additional on-site work: free-text lines without prices, with signature and PDF export."}
        actions={
          <div className="flex gap-2">
            <Link to="/quotes">
              <Btn variant="secondary" data-testid="back-to-quotes"><FileText size={16} /> {lang === "de" ? "Angebote" : "Quotes"}</Btn>
            </Link>
            {canCreate && (
              <Link to="/aufmasse/new">
                <Btn data-testid="new-aufmass-btn"><Plus size={16} /> {lang === "de" ? "Neues Aufmaß" : "New Aufmaß"}</Btn>
              </Link>
            )}
          </div>
        }
      />

      {/* tab bar */}
      <QuotesTabBar active="aufmasse" />

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
          <TextInput data-testid="aufmasse-search" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
      </div>

      {loading ? (
        <div className="swiss-card p-8 text-[var(--muted)]">…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={lang === "de" ? "Noch keine Aufmaße" : "No measurements yet"}
          description={lang === "de" ? "Erstelle ein Aufmaß für die Baustelle." : "Create a measurement for the site."}
          action={canCreate ? <Link to="/aufmasse/new"><Btn data-testid="empty-new-aufmass"><Plus size={16} /> {lang === "de" ? "Aufmaß erstellen" : "Create Aufmaß"}</Btn></Link> : null}
        />
      ) : (
        <div className="swiss-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[var(--bg)] text-[var(--muted)]">
              <tr>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("number")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{lang === "de" ? "Aufmaßdatum" : "Date"}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("title")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("customer")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">Status</th>
                {(canEdit || canDelete || canExport) && <th className="text-right font-medium px-5 py-3 label-overline">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((x) => (
                <tr key={x.id} className="border-t border-[var(--border)] hover:bg-[var(--row-hover)] transition-colors fade-up">
                  <td className="px-5 py-3 font-mono">
                    <span>{x.number}</span>
                    {x.has_signed_pdf && (
                      <span
                        title={lang === "de" ? "Signiertes PDF hinterlegt" : "Signed PDF attached"}
                        className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-[var(--brand)]/10 text-[var(--brand)] uppercase tracking-wide"
                        data-testid={`signed-badge-${x.id}`}
                      >
                        {lang === "de" ? "Signiert" : "Signed"}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-[var(--muted)]">{(x.measurement_date || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 font-medium">
                    <Link to={`/aufmasse/${x.id}`} className="hover:underline" data-testid={`aufmass-link-${x.id}`}>
                      {x.title || <span className="text-[var(--muted-2)]">—</span>}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-[var(--muted)]">{x.customer_snapshot?.name || "—"}</td>
                  <td className="px-5 py-3">
                    {x.status === "signed"
                      ? <span className="inline-flex px-2 py-0.5 text-xs rounded-sm bg-[#10B981]/10 text-[#047857]">{lang === "de" ? "Unterschrieben" : "Signed"}</span>
                      : <span className="inline-flex px-2 py-0.5 text-xs rounded-sm bg-[var(--row-hover)] text-[var(--muted)]">{lang === "de" ? "Entwurf" : "Draft"}</span>
                    }
                  </td>
                  {(canEdit || canDelete || canExport) && (
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {canExport && <Btn variant="ghost" onClick={() => downloadPdf(x.id, x.number)} className="h-8 px-2" data-testid={`pdf-aufmass-${x.id}`}><FilePdf size={14} /></Btn>}
                        {canCreateInvoice && <Btn variant="ghost" onClick={() => convertToInvoice(x.id, x.number)} className="h-8 px-2" title={lang === "de" ? "Rechnung erstellen" : "Create invoice"} data-testid={`invoice-aufmass-${x.id}`}><Receipt size={14} /></Btn>}
                        {canEdit && <Link to={`/aufmasse/${x.id}`}><Btn variant="ghost" className="h-8 px-2" data-testid={`edit-aufmass-${x.id}`}><PencilSimple size={14} /></Btn></Link>}
                        {canDelete && <Btn variant="ghost" onClick={() => del(x.id, x.number)} className="h-8 px-2 text-[#FF3B30] hover:bg-[#FF3B30]/5" data-testid={`delete-aufmass-${x.id}`}><Trash size={14} /></Btn>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
